import React, { useReducer, useCallback } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import { LoginModal } from '../auth';
import { useTranslation } from '@/hooks/useTranslation';

import { translateTabReducer, initialTranslateTabState } from './translateTabReducer';
import { useLanguageDetection } from './useLanguageDetection';
import { useTranslateActions } from './useTranslateActions';
import { TranslationForm } from './TranslationForm';
import { AIBoostPanel } from './AIBoostPanel';
import { FileUploadSection } from './FileUploadSection';

export const TranslateTab: React.FC = () => {
  const { t } = useTranslation();

  const [state, dispatch] = useReducer(translateTabReducer, initialTranslateTabState);

  const {
    sourceText,
    sourceLanguage,
    targetLanguages,
    isTranslating,
    translationResult,
    detectedLanguageInfo,
    isDetecting,
    enableAiBoost,
    llmProvider,
    fileMode,
  } = state;

  // LLM model is owned by AIBoostPanel (via useLLMModels); sync it here via
  // a ref so handleTranslate can read the current value without re-creating callbacks.
  const llmModelRef = React.useRef<string>('');
  const handleLlmModelChange = useCallback((model: string) => {
    llmModelRef.current = model;
  }, []);

  // Language auto-detection side-effect
  useLanguageDetection({ sourceText, sourceLanguage, dispatch });

  // All async handlers + auth modal state
  const {
    handleTranslate,
    handleCopy,
    handleDownload,
    handleSwap,
    handleReset,
    isLoginModalOpen,
    loginMessage,
    closeLoginModal,
  } = useTranslateActions({ state, dispatch, llmModelRef });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl mb-4">
          <Languages className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">
          {t('translate.title', { defaultValue: 'Text Translation' })}
        </h2>
        <p className="text-lg text-white/80">
          {t('translate.subtitle', {
            defaultValue: 'Translate text to 146+ languages using NLLB-200',
          })}
        </p>
      </div>

      {/* Main content */}
      <div className="space-y-6">
        {/* Language selectors + text areas */}
        <TranslationForm
          sourceText={sourceText}
          sourceLanguage={sourceLanguage}
          targetLanguages={targetLanguages}
          isTranslating={isTranslating}
          isDetecting={isDetecting}
          translationResult={translationResult}
          detectedLanguageInfo={detectedLanguageInfo}
          onSourceTextChange={(text) => dispatch({ type: 'SET_SOURCE_TEXT', payload: text })}
          onSourceLanguageChange={(lang) =>
            dispatch({ type: 'SET_SOURCE_LANGUAGE', payload: lang })
          }
          onTargetLanguagesChange={(langs) =>
            dispatch({ type: 'SET_TARGET_LANGUAGES', payload: langs })
          }
          onSwap={handleSwap}
          onCopy={handleCopy}
          onDownload={handleDownload}
        />

        {/* Options panel */}
        <div className="bg-black/30 backdrop-blur-md rounded-lg border border-white/10 p-6">
          <div className="space-y-4">
            <AIBoostPanel
              enableAiBoost={enableAiBoost}
              llmProvider={llmProvider}
              isTranslating={isTranslating}
              onEnableAiBoostChange={(enabled) =>
                dispatch({ type: 'SET_ENABLE_AI_BOOST', payload: enabled })
              }
              onLlmProviderChange={(provider) =>
                dispatch({ type: 'SET_LLM_PROVIDER', payload: provider })
              }
              onLlmModelChange={handleLlmModelChange}
            />

            <FileUploadSection
              fileMode={fileMode}
              isTranslating={isTranslating}
              translationResult={translationResult}
              onFileModeChange={(enabled) => dispatch({ type: 'SET_FILE_MODE', payload: enabled })}
              onDownload={handleDownload}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-center space-x-4">
          <button
            type="button"
            onClick={handleTranslate}
            disabled={isTranslating || !sourceText.trim() || targetLanguages.length === 0}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
          >
            {isTranslating ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>
                  {t('translate.buttons.translating', { defaultValue: 'Translating...' })}
                </span>
              </>
            ) : (
              <>
                <Languages className="h-5 w-5" />
                <span>{t('translate.buttons.translate', { defaultValue: 'Translate' })}</span>
              </>
            )}
          </button>

          {translationResult && (
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white/80 font-medium rounded-lg hover:bg-white/20 transition-colors"
            >
              {t('translate.buttons.reset', { defaultValue: 'Clear' })}
            </button>
          )}
        </div>

        {/* Processing time */}
        {translationResult && translationResult.processing_time_seconds && (
          <div className="text-center text-sm text-white/70">
            {t('translate.results.processingTime', {
              defaultValue: 'Translated in {time}s',
              time: translationResult.processing_time_seconds.toFixed(2),
            })}
          </div>
        )}
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />
    </div>
  );
};

export default TranslateTab;
